import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Skeleton } from '@/components/ui/skeleton'
import { displayMapper } from '@/lib/display-mapper'
import type { DashboardSummaryDto } from './types'
import type { GroupConcentration, TopMover } from './leak-site-utils'

/**
 * Right rail for the Leak Sites page. Every panel is backed by real data:
 *   - Top movers + group concentration derive from `/leak-sites/groups`
 *     recent (90d) claim counts.
 *   - Watched exposure only appears when the caller has saved sector/region
 *     preferences (`scopedToUserPrefs` from `/leak-sites/dashboard-summary`).
 *   - Data caveats are standing editorial guidance.
 *
 * The design's "source health" panel is intentionally omitted — mirror /
 * parser health is not part of the current contract and we don't fabricate it.
 */

function RailPanel({
  label,
  accent,
  children,
}: {
  label: string
  accent?: boolean
  children: ReactNode
}) {
  return (
    <section
      aria-label={label}
      className={
        accent
          ? 'rounded-lg border border-primary/30 bg-primary/5 p-4'
          : 'rounded-lg border border-border bg-card p-4'
      }
    >
      <h3 className="mb-3 text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </h3>
      {children}
    </section>
  )
}

export interface LeakSitesContextRailProps {
  topMovers: TopMover[]
  concentration: GroupConcentration
  summary?: DashboardSummaryDto
  isLoading: boolean
}

export function LeakSitesContextRail({
  topMovers,
  concentration,
  summary,
  isLoading,
}: LeakSitesContextRailProps) {
  return (
    <div className="flex flex-col gap-4">
      <RailPanel label="Data caveats">
        <ul className="list-disc space-y-1.5 pl-4 text-xs leading-relaxed text-muted-foreground">
          <li>
            Claims are unverified group assertions; a named victim may dispute
            or pre-empt them.
          </li>
          <li>
            Discovery lags publication — timestamps reflect first aggregator
            capture, not the breach itself.
          </li>
          <li>
            Sector and region mapping is heuristic; confirm before escalation.
          </li>
        </ul>
      </RailPanel>

      <RailPanel label="Top movers · 90d">
        {isLoading ? (
          <div className="space-y-3" role="status" aria-label="Loading top movers">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
        ) : topMovers.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No group activity in the last 90 days.
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {topMovers.map((m) => (
              <li key={m.id}>
                <div className="flex items-center justify-between gap-2 text-[13px]">
                  <span className="flex items-center gap-2 truncate">
                    <span
                      className={
                        m.active
                          ? 'h-1.5 w-1.5 flex-none rounded-full bg-emerald-500'
                          : 'h-1.5 w-1.5 flex-none rounded-full bg-muted-foreground'
                      }
                      aria-hidden="true"
                    />
                    {m.threatActorId ? (
                      <Link
                        to={`/threat-actors/${m.threatActorId}`}
                        className="truncate font-mono text-foreground hover:text-primary hover:underline"
                      >
                        {m.name}
                      </Link>
                    ) : (
                      <span className="truncate font-mono text-foreground">
                        {m.name}
                      </span>
                    )}
                  </span>
                  <span className="flex-none font-mono text-xs text-muted-foreground">
                    {m.count.toLocaleString('en-GB')}
                  </span>
                </div>
                <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${Math.round(m.share * 100)}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </RailPanel>

      {summary?.scopedToUserPrefs ? (
        <RailPanel label="Watched exposure" accent>
          <dl className="flex flex-col gap-2.5 text-[13px]">
            {summary.topSector ? (
              <div className="flex items-center justify-between gap-2">
                <dt className="text-muted-foreground">
                  {displayMapper.sector(summary.topSector.sector)}
                </dt>
                <dd className="font-mono text-primary">
                  {summary.topSector.count} hits
                </dd>
              </div>
            ) : null}
            {summary.topCountry ? (
              <div className="flex items-center justify-between gap-2">
                <dt className="text-muted-foreground">
                  {displayMapper.country(summary.topCountry.country)}
                </dt>
                <dd className="font-mono text-primary">
                  {summary.topCountry.count} hits
                </dd>
              </div>
            ) : null}
            <p className="pt-0.5 text-xs text-muted-foreground">
              Scoped to your saved sectors and regions over the last 7 days.
            </p>
          </dl>
        </RailPanel>
      ) : null}

      <RailPanel label="Group concentration · 90d">
        {isLoading ? (
          <Skeleton className="h-2 w-full" />
        ) : concentration.totalRecent === 0 ? (
          <p className="text-xs text-muted-foreground">
            No group activity to measure.
          </p>
        ) : (
          <>
            <div
              className="mb-3 flex h-2 overflow-hidden rounded-full bg-muted"
              aria-hidden="true"
            >
              {concentration.segments.map((s) => (
                <div
                  key={s.id}
                  className="h-full bg-primary/80 first:rounded-l-full"
                  style={{ width: `${Math.round(s.share * 100)}%` }}
                />
              ))}
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Top {concentration.segments.length}{' '}
              {concentration.segments.length === 1 ? 'group accounts' : 'groups account'}{' '}
              for{' '}
              <span className="font-mono text-foreground">
                {Math.round(concentration.topShare * 100)}%
              </span>{' '}
              of recent claims.
            </p>
          </>
        )}
      </RailPanel>
    </div>
  )
}
